/* import __COLOCATED_TEMPLATE__ from './collapsible-list.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { schedule } from '@ember/runloop';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type Guideline from 'embercom/models/ai-agent/guidelines';
// @ts-ignore no type declaration available for ember-copy
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type { GuidelineCategory } from 'embercom/models/ai-agent/guidelines';
import { states } from 'embercom/models/data/matching-system/matching-constants';

const VOICE_TO_CHAT_CATEGORY_MAP: Partial<Record<GuidelineCategory, GuidelineCategory>> = {
  tone: 'voice_tone',
  clarification: 'voice_clarification',
  routing: 'voice_routing',
  content: 'voice_content',
};

export enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

export interface Args {
  title: string;
  category: GuidelineCategory;
  description: string;
  guidelines: Guideline[];
  icon: InterfaceIconName;
  maxGuidelineCount: number;
  chartDefinitionsHash: Record<string, Record<Stat, any>>;
  alwaysOpen?: boolean;
  hideHeader?: boolean;
  categoryStampTitle?: string;
  onAskGuideline?: (guideline: Guideline) => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class GuidelinesCollapsibleList extends Component<Signature> {
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare intercomEventService: $TSFixMe;

  @tracked isOpen = true;
  @tracked sideDrawerOpen = false;

  get app(): any {
    return this.appService.app;
  }

  get arrowIconClass(): InterfaceIconName {
    return this.isOpen ? 'small-thin-down-arrow' : 'small-thin-right-arrow';
  }

  get maybeEmptyStateClasses(): string {
    return this.args.guidelines.length ? '' : 'filter grayscale opacity-50';
  }

  get headerOpenClassName(): string {
    return 'collapsible-list__header__open net-new-flag';
  }

  get openSectionId(): string | null {
    return this.aiAgentGuidelineService.openGuidelineId;
  }

  get guidelinesForCategory(): Guideline[] {
    let voiceEquivalent = VOICE_TO_CHAT_CATEGORY_MAP[this.args.category];

    return this.args.guidelines
      .filter(
        (guideline) =>
          !guideline.isDeleted &&
          (guideline.category === this.args.category ||
            (voiceEquivalent && guideline.category === voiceEquivalent)),
      )
      .sort((a, b) => {
        // Draft guidelines come last
        if (a.state === states.draft && b.state !== states.draft) {
          return 1;
        }
        if (b.state === states.draft && a.state !== states.draft) {
          return -1;
        }

        // Otherwise sort by ID in ascending order (highest number last)
        return Number(a.id) - Number(b.id);
      });
  }

  get guidelineCount(): number {
    return this.args.guidelines.filter((guideline) => !guideline.isDeleted).length;
  }

  get limitReached(): boolean {
    return this.guidelineCount >= this.args.maxGuidelineCount;
  }

  get cannotAddNewGuidelines(): boolean {
    return this.limitReached || this.hasUnsavedGuidelineInCategory;
  }

  get hasUnsavedGuidelineInCategory(): boolean {
    return this.args.guidelines.some(
      (guideline) =>
        guideline.category === this.args.category &&
        guideline.state === states.draft &&
        (guideline.hasDirtyAttributes || guideline.title === ''),
    );
  }

  get shouldShowEmptyState(): boolean {
    return this.guidelinesForCategory.length === 0 && this.args.category !== 'other';
  }

  @action
  toggleOpenState(): void {
    if (!this.args.alwaysOpen) {
      this.isOpen = !this.isOpen;
    }
  }

  @action
  onOpenSectionChange(sectionId: string) {
    this.aiAgentGuidelineService.setOpenGuidelineId(sectionId);
  }

  @action
  async createNewGuideline(): Promise<void> {
    let newGuideline: Guideline = await this.aiAgentGuidelineService.createNewGuideline(
      this.args.category,
      null,
    );

    this.openAccordionSection(newGuideline);
  }

  @action
  openAccordionSection(guideline: Guideline) {
    this.isOpen = true;
    this.aiAgentGuidelineService.setOpenGuidelineId(`guideline:${guideline.id}`);
  }

  @action
  scrollToGuideline(element: HTMLElement, [shouldScroll]: [boolean]) {
    if (shouldScroll) {
      schedule('afterRender', () => {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::CollapsibleList': typeof GuidelinesCollapsibleList;
  }
}
